<div class="properties-panel">
	<!-- Header -->
	<div class="panel-header">
		<h3 class="panel-title" matTooltip="Configure the selected field's properties">
			<mat-icon>tune</mat-icon>
			<span>Properties</span>
		</h3>
	</div>

	<!-- No Selection State -->
	<div class="no-selection" *ngIf="!hasSelectedField">
		<mat-icon>touch_app</mat-icon>
		<h4>No field selected</h4>
		<p>Select a field from the canvas to edit its properties</p>
	</div>

	<!-- Properties Form -->
	<div class="properties-content" *ngIf="hasSelectedField">
		<!-- Field Type Badge -->
		<div class="field-type-badge">
			<mat-chip-listbox>
				<mat-chip-option selected disabled matTooltip="Field type">{{ fieldType }}</mat-chip-option>
			</mat-chip-listbox>
		</div>

		<form [formGroup]="propertiesForm" class="properties-form">
			<!-- Basic Properties -->
			<mat-expansion-panel [expanded]="expandedSections.basic" class="section-panel">
				<mat-expansion-panel-header matTooltip="Configure field label, key, placeholder and description">
					<mat-panel-title>
						<mat-icon>info</mat-icon>
						<span>Basic Properties</span>
					</mat-panel-title>
				</mat-expansion-panel-header>

				<div class="form-section">
					<mat-form-field appearance="outline" class="formly-full-width">
						<mat-label>Key</mat-label>
						<input matInput formControlName="key" placeholder="fieldKey" />
						<mat-hint>Unique identifier for the field</mat-hint>
					</mat-form-field>

					<mat-form-field appearance="outline" class="formly-full-width">
						<mat-label>Label</mat-label>
						<input matInput formControlName="label" placeholder="Enter label" />
						<mat-hint>Display label for the field</mat-hint>
					</mat-form-field>

					<mat-form-field appearance="outline" class="formly-full-width">
						<mat-label>Placeholder</mat-label>
						<input matInput formControlName="placeholder" placeholder="Enter placeholder" />
						<mat-hint>Placeholder text (for input fields)</mat-hint>
					</mat-form-field>

					<mat-form-field appearance="outline" class="formly-full-width">
						<mat-label>Description</mat-label>
						<textarea
							matInput
							formControlName="description"
							rows="2"
							placeholder="Enter description"
						></textarea>
						<mat-hint>Help text or description</mat-hint>
					</mat-form-field>

					<mat-form-field appearance="outline" class="formly-full-width">
						<mat-label>Hint</mat-label>
						<input matInput formControlName="hint" placeholder="Enter hint" />
						<mat-hint>Hint text below the field</mat-hint>
					</mat-form-field>

					<mat-form-field appearance="outline" class="formly-full-width" *ngIf="showInputTypeField">
						<mat-label>Input Type</mat-label>
						<mat-select formControlName="inputType">
							<mat-option value="text">Text</mat-option>
							<mat-option value="email">Email</mat-option>
							<mat-option value="tel">Telephone</mat-option>
							<mat-option value="number">Number</mat-option>
							<mat-option value="password">Password</mat-option>
							<mat-option value="url">URL</mat-option>
						</mat-select>
					</mat-form-field>
				</div>
			</mat-expansion-panel>

			<!-- Validation Properties -->
			<mat-expansion-panel [expanded]="expandedSections.validation" class="section-panel">
				<mat-expansion-panel-header
					matTooltip="Set validation rules like required, disabled, or exclude from submission"
				>
					<mat-panel-title>
						<mat-icon>check_circle</mat-icon>
						<span>Validation</span>
					</mat-panel-title>
				</mat-expansion-panel-header>

				<div class="form-section">
					<div class="checkbox-group">
						<mat-checkbox formControlName="required" matTooltip="Mark field as required"
							>Required</mat-checkbox
						>
						<mat-checkbox formControlName="disabled" matTooltip="Disable field input"
							>Disabled</mat-checkbox
						>
						<mat-checkbox formControlName="exclude" matTooltip="Exclude field from form submission"
							>Exclude from submission</mat-checkbox
						>
					</div>
				</div>
			</mat-expansion-panel>

			<!-- Styling Properties -->
			<mat-expansion-panel [expanded]="expandedSections.styling" class="section-panel">
				<mat-expansion-panel-header matTooltip="Customize field appearance with CSS classes">
					<mat-panel-title>
						<mat-icon>palette</mat-icon>
						<span>Styling</span>
					</mat-panel-title>
				</mat-expansion-panel-header>

				<div class="form-section">
					<mat-form-field appearance="outline" class="formly-full-width">
						<mat-label>CSS Classes</mat-label>
						<input matInput formControlName="className" placeholder="class-name" />
						<mat-hint>Custom CSS classes (space-separated)</mat-hint>
					</mat-form-field>
				</div>
			</mat-expansion-panel>

			<!-- Advanced Properties -->
			<mat-expansion-panel [expanded]="expandedSections.advanced" class="section-panel">
				<mat-expansion-panel-header
					matTooltip="Configure advanced options like hide expressions and default values"
				>
					<mat-panel-title>
						<mat-icon>code</mat-icon>
						<span>Advanced</span>
					</mat-panel-title>
				</mat-expansion-panel-header>

				<div class="form-section">
					<mat-form-field appearance="outline" class="formly-full-width">
						<mat-label>Hide Expression</mat-label>
						<textarea
							matInput
							formControlName="hideExpression"
							rows="2"
							placeholder="e.g., !model.showField"
						></textarea>
						<mat-hint>JavaScript expression to hide field</mat-hint>
					</mat-form-field>

					<div class="dependency-info" *ngIf="referencedFields.length > 0">
						<div class="dependency-header">
							<mat-icon>device_hub</mat-icon>
							<span>Field Dependencies</span>
						</div>
						<div class="dependency-chips">
							<mat-chip-listbox>
								<mat-chip-option
									*ngFor="let field of referencedFields"
									disabled
									[matTooltip]="'This field depends on: ' + field"
								>
									{{ field }}
								</mat-chip-option>
							</mat-chip-listbox>
						</div>
					</div>

					<mat-form-field appearance="outline" class="formly-full-width">
						<mat-label>Default Value</mat-label>
						<input matInput formControlName="defaultValue" placeholder="Enter default value" />
						<mat-hint>Initial value for the field</mat-hint>
					</mat-form-field>
				</div>
			</mat-expansion-panel>

			<!-- API Configuration -->
			<mat-expansion-panel [expanded]="expandedSections.api" class="section-panel api-section">
				<mat-expansion-panel-header matTooltip="Configure API endpoint to dynamically load field options">
					<mat-panel-title>
						<mat-icon>http</mat-icon>
						<span>API Configuration</span>
						<span *ngIf="hasApiConfig" class="api-indicator" matTooltip="API endpoint configured"></span>
					</mat-panel-title>
				</mat-expansion-panel-header>

				<div class="form-section">
					<div class="api-url-section">
						<mat-form-field appearance="outline" class="api-url-field">
							<mat-label>API Endpoint URL</mat-label>
							<input matInput formControlName="apiUrl" placeholder="/api/contract/GetStates" />
							<mat-hint>URL to fetch options from</mat-hint>
						</mat-form-field>
						<button
							mat-raised-button
							color="primary"
							class="test-api-button"
							(click)="testApiEndpoint()"
							[disabled]="apiTesting || !propertiesForm.get('apiUrl')?.value"
							matTooltip="Test API endpoint"
						>
							<mat-icon *ngIf="!apiTesting">play_arrow</mat-icon>
							<mat-spinner *ngIf="apiTesting" diameter="20"></mat-spinner>
							<span *ngIf="!apiTesting">Test</span>
							<span *ngIf="apiTesting">Testing...</span>
						</button>
					</div>

					<!-- API Test Results -->
					<div class="api-test-results" *ngIf="showApiTestResults">
						<div class="test-results-header">
							<mat-icon>{{ apiTestError ? 'error' : 'check_circle' }}</mat-icon>
							<span>Test Results</span>
							<button mat-icon-button (click)="closeApiTestResults()" matTooltip="Close">
								<mat-icon>close</mat-icon>
							</button>
						</div>

						<div class="test-results-content">
							<!-- Error State -->
							<div class="test-error" *ngIf="apiTestError">
								<mat-icon>warning</mat-icon>
								<span>{{ apiTestError }}</span>
							</div>

							<!-- Success State -->
							<div class="test-success" *ngIf="apiTestResult && !apiTestError">
								<div class="success-summary">
									<mat-icon>check_circle</mat-icon>
									<span>API returned {{ apiTestResultCount }} item(s)</span>
								</div>
								<div class="result-preview">
									<div class="preview-header">Response Preview:</div>
									<pre class="json-preview">{{ apiTestResultJson }}</pre>
								</div>
							</div>
						</div>
					</div>

					<mat-form-field appearance="outline" class="formly-full-width">
						<mat-label>Label Field</mat-label>
						<input matInput formControlName="apiItemFieldLabel" placeholder="label" />
						<mat-hint>Field name to use for display label</mat-hint>
					</mat-form-field>

					<mat-form-field appearance="outline" class="formly-full-width">
						<mat-label>Value Field</mat-label>
						<input matInput formControlName="apiItemFieldValue" placeholder="value" />
						<mat-hint>Field name to use for option value</mat-hint>
					</mat-form-field>

					<mat-form-field appearance="outline" class="formly-full-width">
						<mat-label>HTTP Method</mat-label>
						<mat-select formControlName="apiMethod">
							<mat-option [value]="null">Default (GET)</mat-option>
							<mat-option value="GET">GET</mat-option>
							<mat-option value="POST">POST</mat-option>
						</mat-select>
					</mat-form-field>

					<mat-form-field appearance="outline" class="formly-full-width">
						<mat-label>Reference Data Name</mat-label>
						<input matInput formControlName="apiRefDataName" placeholder="Optional" />
						<mat-hint>Reference data name if applicable</mat-hint>
					</mat-form-field>

					<div class="checkbox-group">
						<mat-checkbox formControlName="apiOverride">Override static options with API data</mat-checkbox>
					</div>
				</div>
			</mat-expansion-panel>

			<!-- Nested Scripts Configuration -->
			<mat-expansion-panel
				*ngIf="isNestedScriptField"
				[expanded]="expandedSections.nestedScripts"
				class="section-panel nested-scripts-section"
			>
				<mat-expansion-panel-header matTooltip="Configure which scripts to include in this nested script field">
					<mat-panel-title>
						<mat-icon>list_alt</mat-icon>
						<span>Nested Scripts</span>
						<span
							*ngIf="selectedScripts.length > 0"
							class="script-count-badge"
							[matTooltip]="selectedScripts.length + ' script(s) configured'"
						>
							{{ selectedScripts.length }}
						</span>
					</mat-panel-title>
				</mat-expansion-panel-header>

				<div class="form-section">
					<!-- Selected Scripts -->
					<div class="selected-scripts-container">
						<div class="section-header">
							<span class="header-text">Selected Scripts ({{ selectedScripts.length }})</span>
							<mat-hint>Scripts will be executed in the order shown below</mat-hint>
						</div>

						<div class="selected-scripts-list" *ngIf="selectedScripts.length > 0">
							<div class="script-item" *ngFor="let script of selectedScripts; let i = index">
								<div class="script-order">{{ i + 1 }}</div>
								<div class="script-info">
									<div class="script-name">{{ script.name }}</div>
									<div class="script-meta">
										<span class="script-id">ID: {{ script.scriptId }}</span>
										<span class="script-description" *ngIf="script.description">{{
											script.description
										}}</span>
									</div>
								</div>
								<div class="script-actions">
									<button
										mat-icon-button
										(click)="moveScriptUp(i)"
										[disabled]="i === 0"
										matTooltip="Move up"
									>
										<mat-icon>arrow_upward</mat-icon>
									</button>
									<button
										mat-icon-button
										(click)="moveScriptDown(i)"
										[disabled]="i === selectedScripts.length - 1"
										matTooltip="Move down"
									>
										<mat-icon>arrow_downward</mat-icon>
									</button>
									<button
										mat-icon-button
										color="warn"
										(click)="removeScript(i)"
										matTooltip="Remove script"
									>
										<mat-icon>delete</mat-icon>
									</button>
								</div>
							</div>
						</div>

						<div class="empty-state" *ngIf="selectedScripts.length === 0">
							<mat-icon>info</mat-icon>
							<p>No scripts selected. Search and add scripts from the list below.</p>
						</div>
					</div>

					<!-- Available Scripts -->
					<div class="available-scripts-container">
						<div class="section-header">
							<span class="header-text">Available Scripts</span>
							<mat-form-field appearance="outline" class="script-search-field">
								<mat-label>Search scripts</mat-label>
								<input
									matInput
									[(ngModel)]="scriptSearchTerm"
									placeholder="Search by name, ID, or description"
								/>
								<mat-icon matPrefix>search</mat-icon>
								<button
									mat-icon-button
									matSuffix
									*ngIf="scriptSearchTerm"
									(click)="scriptSearchTerm = ''"
									matTooltip="Clear search"
								>
									<mat-icon>close</mat-icon>
								</button>
							</mat-form-field>
						</div>

						<div class="loading-state" *ngIf="loadingScripts">
							<mat-spinner diameter="40"></mat-spinner>
							<p>Loading scripts...</p>
						</div>

						<div
							class="available-scripts-list"
							*ngIf="!loadingScripts && filteredAvailableScripts.length > 0"
						>
							<div
								class="script-item formly-clickable"
								*ngFor="let script of filteredAvailableScripts"
								(click)="addScript(script)"
							>
								<div class="script-info">
									<div class="script-name">{{ script.name }}</div>
									<div class="script-meta">
										<span class="script-id">ID: {{ script.scriptId }}</span>
										<span class="script-description" *ngIf="script.description">{{
											script.description
										}}</span>
									</div>
								</div>
								<button
									mat-icon-button
									color="primary"
									(click)="addScript(script); $event.stopPropagation()"
									matTooltip="Add script"
								>
									<mat-icon>add</mat-icon>
								</button>
							</div>
						</div>

						<div class="empty-state" *ngIf="!loadingScripts && filteredAvailableScripts.length === 0">
							<mat-icon>search_off</mat-icon>
							<p *ngIf="scriptSearchTerm">No scripts found matching "{{ scriptSearchTerm }}"</p>
							<p *ngIf="!scriptSearchTerm">All available scripts have been added</p>
						</div>
					</div>
				</div>
			</mat-expansion-panel>
		</form>

		<!-- Action Buttons -->
		<div class="action-buttons">
			<button mat-raised-button color="primary" (click)="onApplyChanges()" class="apply-button">
				<mat-icon>check</mat-icon>
				Apply Changes
			</button>
			<button mat-stroked-button color="warn" (click)="onDeleteField()" class="delete-button">
				<mat-icon>delete</mat-icon>
				Delete Field
			</button>
		</div>
	</div>
</div>
